import { CameraIcon } from "@heroicons/react/20/solid";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { Notes } from "../../../../components/dashboard/notes/notes";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import {
  ArrowPathRoundedSquareIcon,
  BuildingOfficeIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentTextIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import {
  deleteChecklistItem,
  fetchChecklist,
  updateChecklist,
} from "../../../../api/quality/checklist";
import { CustomerTag } from "../../../../components/utils/moduleTags/customerTag";
import { ProjectTag } from "../../../../components/utils/moduleTags/projectTag";
import { EmployeeTag } from "../../../../components/utils/moduleTags/employeeTag";
import { VehicleTag } from "../../../../components/utils/moduleTags/vehicleTag";

import {
  customerHeaderAction,
  dateToString,
  dispatchWithToast,
} from "../../../../components/Utils";
import { HistoriesModalButton } from "../../../../components/dashboard/histories/modal-button";
import { DocumentViewer } from "../../../../components/dashboard/Media/documentViewer";
import { Popup } from "../../../../components/utils/Div";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import SimplePrompt from "../../../../components/utils/simplePrompt";
import { ProjectFolderTag } from "../../../../components/utils/moduleTags/projectFolderTag";
import ChecklistDialog from "../../../../components/dashboard/quality/checklists/checklistDialog";
import { ChecklistTypeTag } from "../../../../components/utils/moduleTags/checklistTypeTag";
import { Form, Formik } from "formik";
import { FormikSaveBar } from "../../../../components/utils/FormikSaveBar";
import ChecklistItemLines from "../../../../components/dashboard/quality/checklists/checklistItemLines";
import { updateChecklistAsync } from "../../../../slices/quality/checklist";
import {
  ChecklistApprovedStatus,
  ChecklistStatus,
} from "../../../../components/dashboard/quality/checklists/utils";
import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import { ChecklistPrintDialog } from "../../../../components/dashboard/quality/checklists/checklistPrint";
import { PMNotice } from "../../../../components/utils/notifications";
import ChecklistItemLinesCompactV3 from "../../../../components/dashboard/quality/checklists/checklistItemLinesCompactV3";
import ChecklistStickyFillChrome from "../../../../components/dashboard/quality/checklists/ChecklistStickyFillChrome";
import { collectChecklistGaps, itemRowResolved } from "../../../../components/dashboard/quality/checklists/checklistResponseConfig";

export const ChecklistView = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    checklist: {},
  });

  const getChecklist = () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    try {
      fetchChecklist(id).then((data) => {
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          checklist: data?.data,
        }));
      });
    } catch {
      toast.error("Something went wrong when trying to load the checklist");
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
        error: true,
      }));
    }
  };

  useEffect(() => {
    getChecklist();
  }, [dispatch, id]);

  const handleOnRefresh = (data) => {
    if (data?.id) {
      setItemState((prevState) => ({
        ...prevState,
        checklist: data,
      }));
    } else {
      getChecklist();
    }
  };

  function handleNewChecklistItems(data) {
    const newChecklistItem = {
      ...checklist,
      items: [...checklist.items, data],
    };

    setItemState((prevState) => ({
      ...prevState,
      checklist: newChecklistItem,
    }));
  }

  const [checklistItemDeletePromptOpen, setChecklistItemDeletePromptOpen] =
    useState(false);
  const [selectedChecklistItem, setSelectedChecklistItem] = useState(null);

  const promptDeleteChecklistItem = (item) => {
    setSelectedChecklistItem(item);
    setChecklistItemDeletePromptOpen(true);
  };

  const handleDeleteChecklistItem = async () => {
    const payload = {
      checklist_id: checklist?.id,
      id: selectedChecklistItem?.id,
    };
    const response = await deleteChecklistItem(payload);

    if (response?.status === 200) {
      setChecklistItemDeletePromptOpen(false);
      toast.success("Innlegget ble slettet");

      const newChecklistItems = checklist?.items.filter(
        (checklistItem) => checklistItem.id !== selectedChecklistItem?.id
      );

      const newChecklist = {
        ...checklist,
        items: newChecklistItems,
      };

      setItemState((prevState) => ({
        ...prevState,
        checklist: newChecklist,
      }));

      setChecklistItemDeletePromptOpen(false);
      setSelectedChecklistItem(null);
    }
  };

  const { checklist, isLoading, error, errors } = itemState;

  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [checklistSendDialogOpen, setReportSendDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  const canApprove = useUserHasAccess({ role: "checklist", action: "approve" });

  const headerMeta = [
    {
      component: <ChecklistStatus checklist={checklist} />,
      label: "Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      component: <ChecklistApprovedStatus checklist={checklist} />,
      label: "Approved Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: false,
    },
    customerHeaderAction(checklist?.customer),
    {
      label: checklist?.project?.displayTitle,
      description: "",
      path: "/dashboard/projects/" + checklist?.project?.id,
      action: null,
      icon: BuildingOfficeIcon,
      textColor: "text-teal-700",
      hidden: checklist?.project?.id ? false : true,
    },
  ];

  const headerActions = [
    {
      component: (
        <HistoriesModalButton
          module={"checklist"}
          module_param={checklist?.id}
        />
      ),
      label: "History",
      description: "",
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-gray-600",
      bgColor: "bg-gray-50",
      hidden: false,
      menu: false,
    },
    {
      component: (
        <div className="px-1">
          <Notes module={"checklist"} module_param={checklist?.id} />
        </div>
      ),
      label: "Notes",
      description: "",
      path: null,
      action: null,
      icon: DocumentTextIcon,
      textColor: "text-gray-600",
      bgColor: "bg-gray-50",
      hidden: false,
      menu: false,
    },
    {
      component: null,
      label: "Print",
      description: "",
      path: null,
      action: () => setPrintDialogOpen(true),
      icon: PrinterIcon,
      textColor: "text-gray-600",
      bgColor: "bg-gray-50",
      hidden: false,
      menu: true,
    },
    {
      component: null,
      label: "Delete",
      description: "",
      path: null,
      action: () => handleDeleteChecklist(),
      icon: TrashIcon,
      textColor: "text-red-500",
      bgColor: "bg-red-50",
      hidden: false,
      menu: true,
    },
  ];

  const handleChecklistItemUpdate = (data, formik) => {
    formik.setFieldValue("items", data);
  };

  /** Every non-deleted line must be approved or registered as deviation (matches backend item checks). */
  const checklistItemsAllResolved = (items) => {
    if (!items?.length) return false;
    const active = items.filter((row) => !row.delete);
    if (active.length === 0) return false;
    return active.every((item) => itemRowResolved(item));
  };

  const handleFinalizeChecklist = (formik) => {
    if (formik.isSubmitting) return;
    if (!checklistItemsAllResolved(formik.values.items)) return;

    formik.setSubmitting(true);
    const actionPromise = dispatchWithToast(
      dispatch(
        updateChecklistAsync({
          ...formik.values,
          finished: true,
        })
      ),
      (data) => handleRefresh(data)
    );
    Promise.resolve(actionPromise).finally(() => {
      formik.setSubmitting(false);
    });
  };

  const ChecklistItem = ({ item }) => {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <DocumentTextIcon className="w-3 h-3 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600">
                    Added {dateToString(item?.created_at)} by{" "}
                    <span className="font-medium text-gray-900">
                      {item?.created_by?.displayTitle}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <RoleOnly role="checklists" action="destroy">
              <Popup content="Delete item">
                <button
                  type="button"
                  onClick={() => promptDeleteChecklistItem(item)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200 rounded hover:bg-red-50"
                >
                  <TrashIcon className="h-3 w-3" />
                </button>
              </Popup>
            </RoleOnly>
          </div>
        </div>

        <div className="px-4 py-3">
          <p className="text-xs text-gray-700 leading-relaxed">
            {item?.description}
          </p>
        </div>

        {item?.documents && item.documents.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
            <DocumentViewer pathType="quickview" documents={item?.documents} />
          </div>
        )}
      </div>
    );
  };

  const handleRefresh = (data) => {
    if (data?.id) {
      setItemState((prevState) => ({
        ...prevState,
        checklist: data,
      }));
    }
  };

  const handleDeleteChecklist = () => {};

  // A render helper, not a nested component: autosave updates must keep the
  // same Formik/editor tree mounted so focus and the in-flight queue survive.
  const renderContent = () => {
    if (isLoading) {
      return <ResourceLoading size="base" />;
    }

    if (error) return <ResourceError errors={errors} />;

    if (!checklist?.id) {
      return <ResourceEmpty />;
    }

    return (
      <div className="space-y-6">
        {/* Details Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-4 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                  <EyeIcon className="w-4 h-4 text-teal-600" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium text-gray-900">
                  Checklist Details
                </h3>
                <p className="text-xs text-gray-500">
                  Information about the checklist
                </p>
              </div>
            </div>
          </div>

          <div className="px-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ProjectTag
                project={checklist?.project}
                folder={checklist?.folder}
                onRefresh={handleOnRefresh}
              />

              <CustomerTag
                customer={checklist?.customer}
                onRefresh={handleOnRefresh}
              />

              <ProjectFolderTag
                label="Folder"
                folder={checklist?.folder}
                onRefresh={handleOnRefresh}
              />

              <EmployeeTag
                label="Responsible Executive"
                employee={checklist?.responsible_executive}
                onRefresh={handleOnRefresh}
              />

              <ChecklistTypeTag
                checklist_type={checklist?.checklist_type}
                onRefresh={handleOnRefresh}
              />

              <VehicleTag
                vehicle={checklist?.vehicle}
                onRefresh={handleOnRefresh}
              />
            </div>
          </div>
        </div>

        {/* Checklist Items Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-4 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium text-gray-900">
                  Checklist Items
                </h3>
                <p className="text-xs text-gray-500">
                  Manage and update checklist items
                </p>
              </div>
            </div>
          </div>

          <Formik
            enableReinitialize
            initialValues={{
              id: checklist?.id || null,
              items: checklist?.items || [],
              finished: checklist?.finished || false,
              approved: checklist?.approved || false,
              submit: null,
            }}
            onSubmit={(values, helpers) => {
              try {
                dispatchWithToast(
                  dispatch(updateChecklistAsync(values)),
                  (data) => handleRefresh(data)
                );

                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
              } catch (err) {
                console.error(err);
                toast.error("Something went wrong...");
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: err.message });
                helpers.setSubmitting(false);
              }
            }}
          >
            {(formik) => (
              <Form>
                <div className="px-4 py-4 space-y-4">
                  {checklist?.is_items_editable !== undefined &&
                    !checklist?.is_items_editable && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg
                              className="h-5 w-5 text-amber-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-amber-800">
                              Checklist is finalized
                            </h3>
                            <div className="mt-2 text-sm text-amber-700">
                              <p>The checklist cannot be modified</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  <ChecklistItemLinesCompactV3
                    checklist={checklist}
                    items={formik.values.items || []}
                    checklistId={checklist?.id}
                    project={checklist?.project}
                    linkedDrawing={checklist?.linked_drawing}
                    editable={checklist?.is_items_editable !== false}
                    disabled={
                      checklist?.is_items_editable !== undefined &&
                      !checklist?.is_items_editable
                    }
                    isAdmin={canApprove}
                    validationErrors={{}}
                    updateChecklistItems={async (id, payload) => {
                      return await updateChecklist({
                        id,
                        items: payload?.checklist?.items || [],
                      });
                    }}
                    onItemsUpdate={(updatedItems) => {
                      handleChecklistItemUpdate(updatedItems, formik);
                      setItemState((prev) => ({
                        ...prev,
                        checklist: { ...prev.checklist, items: updatedItems },
                      }));
                    }}
                    showGapsBar
                    gapsBarClassName="hidden xl:block"
                    gapsStickyTopClassName="top-[var(--dashboard-topbar-height,4rem)]"
                  />
                </div>

                {/* Status Controls */}
                {checklist?.is_items_editable !== false &&
                  (formik.values.finished ||
                    checklistItemsAllResolved(formik.values.items)) && (
                <div className="px-4 py-4 border-t border-gray-100 bg-gray-50">
                  <div
                    className={
                      canApprove &&
                      (formik.values.finished || checklist?.finished)
                        ? "grid grid-cols-1 md:grid-cols-2 gap-4"
                        : "grid grid-cols-1 gap-4"
                    }
                  >
                    <div className="space-y-3">
                      {formik.values.finished ? (
                        <div className="p-3 bg-white rounded-lg border border-gray-200">
                          <h4 className="text-xs font-medium text-gray-900">
                            {t("quality.checklist.view.completionDoneTitle")}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {t("quality.checklist.view.completionDoneDescription")}
                          </p>
                        </div>
                      ) : checklistItemsAllResolved(formik.values.items) ? (
                        <div className="p-3 bg-white rounded-lg border border-gray-200 space-y-3">
                          <div>
                            <h4 className="text-xs font-medium text-gray-900">
                              {t("quality.checklist.view.completionTitle")}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {t("quality.checklist.view.completionDescription")}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={formik.isSubmitting}
                            onClick={() => handleFinalizeChecklist(formik)}
                            className="w-full rounded-md bg-teal-600 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {formik.isSubmitting
                              ? t("quality.checklist.view.completionSaving")
                              : t("quality.checklist.view.completionButton")}
                          </button>
                        </div>
                      ) : null}
                    </div>

                    {canApprove &&
                      (formik.values.finished || checklist?.finished) && (
                      <div className="space-y-3">
                        {!checklist?.can_be_approved ? (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <svg
                                  className="h-4 w-4 text-red-400"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                              <div className="ml-2">
                                <h3 className="text-xs font-medium text-red-800">
                                  Cannot approve checklist
                                </h3>
                                <div className="mt-1 text-xs text-red-700">
                                  <p>
                                    All items must be approved and completed
                                    first.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                            <div>
                              <h4 className="text-xs font-medium text-gray-900">
                                Checklist Approval
                              </h4>
                              <p className="text-xs text-gray-500">
                                Approve the completed checklist
                              </p>
                            </div>
                            <PMSwitch
                              name="approved"
                              label=""
                              {...formik}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                )}

                <FormikSaveBar {...formik} />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  };

  const gapSummary = collectChecklistGaps(checklist?.items || []);
  const compactProgress =
    gapSummary.totalCount > 0
      ? `${gapSummary.resolvedCount}/${gapSummary.totalCount}`
      : null;

  return (
    <>
      <ChecklistStickyFillChrome
        title={checklist?.displayTitle || t("quality.checklist.page.missingTitle")}
        subtitle={t("quality.checklist.page.qualitySubtitle")}
        backTo="/dashboard/quality/checklists"
        progressLabel={compactProgress}
        statusLabel={
          gapSummary.gaps.length === 0
            ? t("quality.checklist.gaps.statusDone")
            : t("quality.checklist.gaps.statusOpen")
        }
        items={checklist?.items || []}
      />
      <DashboardHeader
        title={checklist?.displayTitle || "Missing title"}
        subtitle="Detaljer for sjekkliste (ISO 9001:2015 - kap. 8.5)"
        actions={headerActions}
        meta={headerMeta}
        chromeClassName="hidden xl:block"
        mainButton={{
          label: "Edit",
          action: () => setChecklistDialogOpen(true),
          path: null,
          color: "",
          icon: PencilIcon,
        }}
      >
        <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-6 lg:px-8">
          {renderContent()}
        </div>
      </DashboardHeader>

      <ChecklistDialog
        open={checklistDialogOpen}
        onClose={() => setChecklistDialogOpen(false)}
        selected={checklist}
        onRefresh={handleOnRefresh}
      />

      <SimplePrompt
        title="Delete item"
        description="Are you sure you want to delete this item?"
        onAccept={() => handleDeleteChecklistItem()}
        open={checklistItemDeletePromptOpen}
        onClose={() => setChecklistItemDeletePromptOpen(false)}
      />

      <ChecklistPrintDialog
        checklist={checklist}
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
      />
    </>
  );
};
